export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const getEventPosition = (event, hourHeight) => {
  let startH = 9, startM = 0;
  if (event.time) {
    const [h, m] = event.time.split(":").map(Number);
    startH = h;
    startM = m || 0;
  }

  let endH = startH + 1, endM = startM;
  if (event.end_time) {
    const [h, m] = event.end_time.split(":").map(Number);
    endH = h;
    endM = m || 0;
  }

  const top = (startH + startM / 60) * hourHeight;
  const duration = (endH + endM / 60) - (startH + startM / 60);
  const height = Math.max(duration * hourHeight, 20);

  return { top, height };
};

export const isRecurringOnDate = (event, date) => {
  if (!event.repeat) return false;

  const [sy, sm, sd] = event.date.split("-").map(Number);
  const startDate = new Date(sy, sm - 1, sd);

  // Must be strictly after start date (start date already matched directly)
  if (date <= startDate) return false;

  // Must not exceed repeat_end_date
  if (event.repeat_end_date) {
    const [ey, em, ed] = event.repeat_end_date.split("-").map(Number);
    if (date > new Date(ey, em - 1, ed)) return false;
  }

  if (event.repeat === "daily") return true;
  if (event.repeat === "weekly") return date.getDay() === startDate.getDay();
  if (event.repeat === "monthly") return date.getDate() === startDate.getDate();
  if (event.repeat === "yearly") {
    return (
      date.getMonth() === startDate.getMonth() &&
      date.getDate() === startDate.getDate()
    );
  }
  return false;
};

export const computeOverlapColumns = (events) => {
  if (!events || events.length === 0) return [];

  const toMinutes = (timeStr) => {
    if (!timeStr) return 9 * 60;
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + (m || 0);
  };

  const items = [...events]
    .map((event) => {
      const start = toMinutes(event.time);
      const rawEnd = toMinutes(event.end_time);
      // Ensure end > start to prevent infinite loop when time === end_time or end_time is missing
      const end = rawEnd > start ? rawEnd : start + 60;
      return { event, start, end };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const colEnds = [];
  const assigned = items.map((item) => {
    let col = colEnds.findIndex((endTime) => endTime <= item.start);
    if (col === -1) {
      col = colEnds.length;
      colEnds.push(item.end);
    } else {
      colEnds[col] = item.end;
    }
    return { event: item.event, start: item.start, end: item.end, col };
  });

  // Compute per-cluster totalCols
  const result = [];
  let i = 0;
  while (i < assigned.length) {
    let clusterEnd = assigned[i].end;
    let j = i;
    while (j < assigned.length && assigned[j].start < clusterEnd) {
      clusterEnd = Math.max(clusterEnd, assigned[j].end);
      j++;
    }
    if (j === i) j = i + 1; // safety: always advance to prevent infinite loop
    const totalCols = Math.max(...assigned.slice(i, j).map((a) => a.col)) + 1;
    for (let k = i; k < j; k++) {
      result.push({ event: assigned[k].event, col: assigned[k].col, totalCols });
    }
    i = j;
  }
  return result;
};

export const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  const totalMin = eh * 60 + em - (sh * 60 + sm);
  if (totalMin <= 0) return null;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

export const createStripedGradient = (colors) => {
  if (colors.length === 1) {
    return colors[0];
  }

  const stripeWidth = 100 / colors.length;
  const gradientStops = colors
    .map((color, index) => {
      const start = index * stripeWidth;
      const end = (index + 1) * stripeWidth;
      return `${color} ${start}%, ${color} ${end}%`;
    })
    .join(", ");

  return `linear-gradient(135deg, ${gradientStops})`;
};
